import classNames from 'classnames';
import { isNil } from 'ramda';
import React, { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import defaultThumbnail from 'src/images/thumbnail-default.jpg'; // FIXME cache or pre-cache. When system goes offline it tries to fetch it again and appears as not found image.
import { ConditionalWrapper } from 'src/js/component/base/form/conditional-wrapper';
import { useAdaptiveFetch } from 'src/js/hook/use-adaptive-fetch';
import { useMutable } from 'src/js/hook/use-mutable';
import { useStores } from 'src/js/hook/use-stores';
import { StatsInterval } from 'src/js/model/api/request/stream-stat-request';
import { StreamSrtStatsResponse } from 'src/js/model/api/response/stream-srt-stats-response';
import {
  fetchStreamSRTGraph,
  fetchStreamStatistics,
  resetStreamStatistics,
} from 'src/js/pages/streaming/stream/stream-store';
import { StatisticPlotContainer } from 'src/js/pages/statistic/statistic-plot-container';
import { StatisticTable, StatsMode } from 'src/js/pages/statistic/statistic-table';
import { useWatchObject } from 'src/js/store/use-watch';
import { generateDataAuto } from 'src/js/util/automation';

import { Button, ButtonToggleGroup, Label1Med } from '@hai/ui-react';

import { VideoEncoderState } from '../video-encoder/video-encoder-models';
import videoEncoders, { fetchVideoEncoders } from '../video-encoder/video-encoder-store';
import { SRTMode, StreamEncapsulation, StreamState, StreamViewModel } from './stream-models';
import { mapStreamToStatistics } from './stream-statistics-content';
import { streamStateMessage } from './stream-view-model-mapper';
import { constant } from 'src/js/constant';
import { createAndDispatchNotification } from 'src/js/notification/notification-helper';
import { NotificationVariant } from 'src/js/notification/notification';
import { FormCheckbox } from 'src/js/component/base/form/form-checkbox';

interface Props {
  refreshInterval?: number;
  model: StreamViewModel;
  fullpage?: boolean;
  showDetailedButton?: boolean;
  isExpanded?: boolean;
  parent: string;
}

/* extends {id:string|number}*/
interface FirstLineProps {
  children: ReactElement[]; // each mapping to a T element with key = T.id
}

const FirstLineWrapper: (props: FirstLineProps) => React.ReactElement<Props> = ({ children, ...rest }) => {
  return (
    <div className="first-line" {...rest}>
      {children}
    </div>
  );
};


const protocolToShort = (input?: string) => {
  if (!input) {
    return input;
  }
  const matches = /TS over (.+)/.exec(input);
  if (matches) {
    return matches[1].toUpperCase();
  }
  return input;
};

export const StreamStatistics: React.FunctionComponent<Props> = ({
  refreshInterval = constant.defaultRefreshInterval,
  model,
  fullpage = false,
  isExpanded = false,
  parent,
}) => {
  const { t } = useTranslation();
  const { sessionStore } = useStores();
  const navigate = useNavigate();

  const [isSRT, setIsSRT] = useState(!isNil(model?.stats?.srt));
  const [graphData, setGraphData] = useMutable<StreamSrtStatsResponse[]>([]);
  const [showRedundancy, setShowRedundancy] = useState(false);
  const [interval, setInterval] = useState(StatsInterval.LAST_5_MINS);
  const [graphInterval, setGraphInterval] = useState(undefined);
  const [mode, setMode] = useState<StatsMode>(fullpage ? StatsMode.withGraph : StatsMode.plainStats);

  const refreshRate = model && (isExpanded || fullpage) ? refreshInterval : undefined;

  useAdaptiveFetch(
    () => {
      if (model.stats?.srt) {
        const redundancyPath: number = undefined;
        let srtRedundancyMode = model.srtRedundancyMode;
        if (model.srtMode === SRTMode.LISTENER) {
          // in listener mode we get the stream redundacy mode from the stats
          srtRedundancyMode = model.stats?.srt.detectedRedundancyMode;
        }
        if (srtRedundancyMode === 'active' || srtRedundancyMode === 'backup') {
          // redundancyPath = 1;
          setShowRedundancy(true);
        } else {
          setShowRedundancy(false);
        }
        return fetchStreamSRTGraph(model, interval, redundancyPath, (updated) => {
          //FIXME test if this works cause it should be done in other plain fetch (not csv)
          if (!isSRT && model.stats.srt) {
            setIsSRT(true);
          }

          if (model.stats.srt) {
            setMode(StatsMode.withGraph); // to leave space for graph
          } else if (mode === StatsMode.withGraph) {
            setMode(StatsMode.plainStats);
          }
          setGraphData(updated);
          if (graphInterval !== interval) {
            setGraphInterval(interval);
          }
        }) /* If we ever fetch both graph path data separately like on MX4E 1.5 and before
        .then((updated) => {
          if (redundancyPath === 1) {
            return fetchStreamSRTGraph(model, interval ?? localTimescale, 2, (updated2) => {
              setGraphData2(updated2);
            });
          }
          return updated;
        })*/;
      } else {
        setIsSRT(false);
      }
      return undefined;
    },
    refreshRate,
    [interval, refreshRate] /* this makes it refresh right away when we change interval */,
  );

  useAdaptiveFetch(() => fetchVideoEncoders(t), refreshRate * 5);

  const [data, setData] = useState(model);
  useAdaptiveFetch(() => fetchStreamStatistics(data, t, setData), fullpage ? undefined : refreshRate, [refreshRate]);
  useEffect(() => {
    setData(model);
  }, [model]);

  const videoEncoder = videoEncoders.find((v) => (model.video ? v.id === model.video[0]?.id : false));

  const showThumbnail = fullpage;

  const [previewEnabled, setPreviewEnabled] = useState(videoEncoder ? videoEncoder.previewEnabled : false);
  useWatchObject(
    videoEncoders,
    () => {
      const videoEncoder = videoEncoders.find((v) => (model.video ? v.id === model.video[0]?.id : false));
      setPreviewEnabled(videoEncoder ? videoEncoder.previewEnabled : false);
    },
    undefined,
    [model],
  );

  // thumbnail refresh
  const [thumbnailDate, setThumbnailDate] = useState<string>(`${new Date().getTime()}`);
  useEffect(() => {
    let endrun = false;
    let timer: any;
    const refreshRate = (videoEncoder?.previewIntervalSec ?? 10) * 1000;
    const triggerRefresh = () => {
      if (!endrun) {
        setThumbnailDate(`${new Date().getTime()}`);
        if (videoEncoder?.previewIntervalSec >= 1) {
          timer = setTimeout(triggerRefresh, refreshRate);
        }
      }
    };

    timer = setTimeout(() => {
      triggerRefresh();
    }, refreshRate);

    return () => {
      // cleanup
      endrun = true;
      clearTimeout(timer);
    };
  }, [videoEncoder?.previewIntervalSec]);

  const handleResetStatistics = () => {
    const resetPromise = resetStreamStatistics(data, t, setData);
    if (resetPromise !== undefined && typeof resetPromise.then === 'function') {
      resetPromise.then((response) => {
        if (response.ok) {
          createAndDispatchNotification(t('statistics.reset.success'), NotificationVariant.SUCCESS, t);
        } else {
          createAndDispatchNotification(t('statistics.reset.error'), NotificationVariant.ERROR, t);
        }
      });
    }
  };

  const showSrtRedundantPathSelector = isSRT && model?.stats?.srt?.path2 && fullpage;

  const [pathSelected, setSelectedList] = useState(['path1', 'path2']);
  const onSelect = (item: any, ev: any) => {
    // console.log('onSelect', item, ev);
    if (ev && ev.currentTarget && ev.currentTarget.checked) {
      const newList = [...pathSelected, item];
      // console.log('setSelectedList', newList);
      setSelectedList(newList);
    } else {
      const newList = pathSelected.filter((loop) => loop !== item);
      // console.log('setSelectedList', newList);
      setSelectedList(newList);
    }
  };

  /*
  const pathSelectedTitle =
    pathSelected.includes('path1') && pathSelected.includes('path2')
      ? t('statistics.pathSelectionAll')
      : pathSelected.includes('path1')
      ? t('statistics.path1')
      : pathSelected.includes('path2')
      ? t('statistics.path2')
      : t('statistics.pathSelectionNone');*/

  const renderGraphHeader = () => {
    return (
      <>
        {mode === StatsMode.withGraph && fullpage && (
          <div className={classNames('mk-graph-dropdowns')} data-auto={generateDataAuto('graph', 'headers')}>
            <div className="d-flex justify-content-between align-items-center" style={{ height: '40px' }}>
              <Label1Med className="hai-gray-06">
                {isSRT
                  ? t('statistics.header.srtStatistics')
                  : t('statistics.header.genericStatistics', { protocol: protocolToShort(model?.encapsulationTextual) })}
              </Label1Med>
              {showSrtRedundantPathSelector && (
                <div className="mk-redundant-path-selector">
                  <div className="mk-checkbox-two-lines cursorPointer">
                    <label>
                      <FormCheckbox
                        name="path1"
                        label={t('statistics.path1')}
                        onChange={(evt: any) => onSelect('path1', evt)}
                        checked={pathSelected.includes('path1')}
                      />
                      <svg
                        height="20px"
                        width="71px"
                        color="white"
                        xmlns="http://www.w3.org/2000/svg"
                        version="1.1"
                        className="strokeLineExample"
                      >
                        <line className="strokeGraphLegend path1" x1="0" x2="71" y1="9" y2="9" />
                      </svg>
                    </label>
                  </div>
                  <div className="mk-checkbox-two-lines cursorPointer">
                    <label>
                      <FormCheckbox
                        name="path2"
                        label={t('statistics.path2')}
                        onChange={(evt: any) => onSelect('path2', evt)}
                        checked={pathSelected.includes('path2')}
                      />

                      <svg
                        height="20px"
                        width="71px"
                        color="white"
                        xmlns="http://www.w3.org/2000/svg"
                        version="1.1"
                        className="strokeLineExample"
                      >
                        <line className="strokeGraphLegend path2" x1="0" x2="71" y1="9" y2="9" />
                      </svg>
                    </label>
                  </div>
                </div>
              )}
              <div className="d-flex align-items-center">
                <ButtonToggleGroup
                  data-auto={generateDataAuto('button_toggle', 'graph')}
                  options={[
                    {
                      label: t(`statistics.chart.timescale.optionsName.${StatsInterval.LAST_5_MINS}`),
                      value: StatsInterval.LAST_5_MINS,
                      active: interval === StatsInterval.LAST_5_MINS,
                    },
                    {
                      label: t(`statistics.chart.timescale.optionsName.${StatsInterval.LAST_60_MINS}`),
                      value: StatsInterval.LAST_60_MINS,
                      active: interval === StatsInterval.LAST_60_MINS,
                    },
                    {
                      label: t(`statistics.chart.timescale.optionsName.${StatsInterval.LAST_24_HOURS}`),
                      value: StatsInterval.LAST_24_HOURS,
                      active: interval === StatsInterval.LAST_24_HOURS,
                    },
                  ]}
                  onOptionClicked={(opt: any) => {
                    setInterval(opt.value);
                  }}
                ></ButtonToggleGroup>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  const streamMsg = streamStateMessage(model, videoEncoder, t);
  const statsUrl =
    parent === 'VIDENC'
      ? `/streaming/stream/stats?videnc=${videoEncoder?.id}`
      : `/streaming/stream/stats?stream=${model?.id}`;
  return (
    <div
      className={classNames('mk-stream-statistics-container', showRedundancy && 'showRedundancy')}
      data-auto={generateDataAuto('statistics', 'container')}
    >
      <div className={classNames(model.encapsulation === StreamEncapsulation.WHIP ? 'mk-stats-container-whip' : 'mk-stats-container', fullpage && 'fulldetails', isSRT && 'srt')}>
        <ConditionalWrapper
          condition={fullpage && (showThumbnail || (isSRT && mode === StatsMode.withGraph))}
          wrapper={(children: any) => <FirstLineWrapper>{children}</FirstLineWrapper>}
        >
          {showThumbnail && (
            <div className="mk-thumbnail-container">
              <div className="thumbnail-header d-flex align-items-center justify-content-left">
                <Label1Med className="hai-gray-06">{t('statistics.thumbnailTitle')}</Label1Med>
              </div>
              <div className="thumbnail-section">
                {previewEnabled &&
                videoEncoder?.stats?.state === VideoEncoderState.WORKING &&
                data.stats?.state === StreamState.RUNNING ? (
                  <img src={`/apis/preview/${videoEncoder?.interface}?t=${thumbnailDate}`} />
                ) : (
                  <div className="thumbnail">
                    <img src={defaultThumbnail} />
                    <div className="overlay unselectable haiui-heading-04-med">{streamMsg}</div>
                  </div>
                )}
              </div>
            </div>
          )}
          {isSRT && mode === StatsMode.withGraph && (
            <StatisticPlotContainer
              width={480}
              height={235}
              model={model}
              header={renderGraphHeader()}
              graphData={graphData()}
              timescale={graphInterval}
              pathSelected={pathSelected}
              showRedundancy={showRedundancy}
              fullpage={fullpage}
            />
          )}
        </ConditionalWrapper>

        <StatisticTable
          mode={fullpage ? StatsMode.plainStats : mode}
          shortlist={!fullpage}
          sections={mapStreamToStatistics(data, fullpage, t)}
          className={!fullpage && 'flexEndContainer'}
          minimumGroup={model.encapsulation === StreamEncapsulation.WHIP ? 2 : 0}
          maximumGroup={model.encapsulation === StreamEncapsulation.WHIP ? 2 : undefined}

        />
      </div>
      {!fullpage && (
        <div className="mk-stats-open-detailed-buttons-row d-flex flexEndContainer">
          {!sessionStore.isUser() && (
            <Button variant="secondary" onClick={handleResetStatistics}>
              {t('general.reset')}
            </Button>
          )}
          {isSRT && (
            <div className="hai-ml-2">
              <a href={statsUrl}>
                <Button
                  variant="secondary"
                  onClick={(e: any) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                    navigate(`/streaming/stream/stats/${model.id}`);
                  }}
                >
                  {t('statistics.button.openDetailed')}
                </Button>
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
